import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Star, Calendar, Settings, LogOut, Edit2, Award, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../context/AuthContext';
import { getOrganizerStats, getUserRatings } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      const [statsRes, reviewsRes] = await Promise.all([
        getOrganizerStats(),
        getUserRatings(user.user_id)
      ]);
      setStats(statsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Failed to load profile data', error);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const hasOrganizerBadge = stats && stats.total_events >= 10 && stats.average_rating >= 4.0;

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-transparent">
        <div className="animate-pulse text-muted-foreground">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <header className="glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <h1 className="text-2xl font-bold">Profile</h1>
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
                    {user.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold">{user.initials}</h2>
                    {hasOrganizerBadge && (
                      <Badge className="bg-secondary text-secondary-foreground">
                        <Award className="w-3 h-3 mr-1" />
                        Top Organizer
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                    <MapPin className="w-3 h-3" />
                    {user.campus} Campus
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Interests */}
              {user.interests?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {user.interests.map(interest => (
                    <Badge key={interest} variant="outline">
                      {interest}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-border/50">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4">Your Stats</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-primary">{stats.total_events}</p>
                    <p className="text-xs text-muted-foreground">Events Created</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-secondary">{stats.events_attended}</p>
                    <p className="text-xs text-muted-foreground">Events Attended</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold">
                      {stats.average_rating > 0 ? (
                        <span className="flex items-center justify-center gap-1">
                          {stats.average_rating.toFixed(1)}
                          <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        </span>
                      ) : '-'}
                    </p>
                    <p className="text-xs text-muted-foreground">Avg Rating</p>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xl font-bold">{stats.total_upvotes}</p>
                    <p className="text-xs text-muted-foreground">Total Upvotes</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xl font-bold">{stats.total_chat_joins}</p>
                    <p className="text-xs text-muted-foreground">Chat Joins</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recent Reviews */}
        {reviews.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-border/50">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4">Recent Reviews</h3>
                <div className="space-y-4">
                  {reviews.slice(0, 3).map(review => (
                    <div key={review.rating_id} className="p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted'}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                      {review.review_text && (
                        <p className="text-sm text-muted-foreground">{review.review_text}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">— {review.user_initials}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-2"
        >
          <Button
            variant="ghost"
            className="w-full justify-between h-14"
            onClick={() => navigate('/my-events')}
            data-testid="my-events-link"
          >
            <span className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-primary" />
              My Events
            </span>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-between h-14"
            onClick={() => navigate('/settings')}
            data-testid="settings-link"
          >
            <span className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-primary" />
              Settings
            </span>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </Button>
          <Separator />
          <Button
            variant="ghost"
            className="w-full justify-start h-14 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Log Out
          </Button>
        </motion.div>
      </div>

      <BottomNav />
    </div>
  );
}
